import React from 'react';
import { useSelector } from "react-redux";
import ArrivalTimeAtFactory from "../../../../components/Operation/ArrivalTimeAtFactory";
import ArrivalTimeAtSite from "../../../../components/Operation/ArrivalTimeAtSite";
import Certain from "../../../../components/Operation/Certain";
import CompletedTime from "../../../../components/Operation/CompletedTime";
import DeadFish from '../../../../components/Operation/DeadFish';
import Delay from "../../../../components/Operation/Delay";
import Duration from "../../../../components/Operation/Duration";
import ExternalSite from "../../../../components/Operation/ExternalSite";
import Factory from "../../../../components/Operation/Factory";
import FishAmount from "../../../../components/Operation/FishAmount";
import FishDisease from "../../../../components/Operation/FishDisease";
import Id from "../../../../components/Operation/Id";
import InvoiceCount from '../../../../components/Operation/InvoiceCount';
import LiceList from "../../../../components/Operation/LiceList";
import Logo from "../../../../components/Operation/Logo";
import NameSpot from "../../../../components/Operation/NameSpot";
import Note from "../../../../components/Operation/Note";
import NoteDetail from "../../../../components/Operation/NoteDetail";
import OperationStatus from "../../../../components/Operation/OperationStatus";
import RequireClosedTransport from "../../../../components/Operation/RequireClosedTransport";
import SiteUnit from "../../../../components/Operation/SiteUnit";
import StartTime from "../../../../components/Operation/StartTime";
import SumWeight from "../../../../components/Operation/SumWeight";
import Weather from "../../../../components/Operation/Weather";
import { selectOneSetting } from "../../../../util/store/selectors";
import { OP_STATUS, OPERATION_TYPE, SITE_TYPE } from "../../../../util/varibles/constants";
import { IHarvest2, IOperation, TOperation2 } from "../../../../util/varibles/interface";
import { PreferenceSetting } from "../../../../util/varibles/userSetting";
import { HARVEST_OPTION_KEY } from "../../../PreferencePage/utils";
import { handleGenerateCustomize } from "../../Plan/Calendar/constants";
import styles from "./style.module.scss";

interface IProps {
    data: IOperation
    isOwn: boolean
    status: OP_STATUS
}

const HarvestView: React.FC<IProps> = ({ data, isOwn, status }) => {
    const { vessel, operation, activity_log } = data;
    const harvestOperation = operation as IHarvest2;
    const totalDischargedFish = (harvestOperation.summary ?? []).reduce((unitTotal, item) => {
        return unitTotal + item.actual.tanks.reduce((tankTotal, tank) => {
            return tankTotal + tank.discharge.reduce((dischargeTotal, discharge) => {
                return dischargeTotal + discharge.fish_amount;
            }, 0);
        }, 0);
    }, 0);
    const customizeHarvest = useSelector(selectOneSetting(PreferenceSetting.CUSTOMIZE_HARVEST));
    const { keys = {}, isSection1, isSection2 } = handleGenerateCustomize(customizeHarvest);
    return <>
        <div className={`${styles.title} !overflow-visible`}>
            <div className={styles.left}>
                <Logo type={OPERATION_TYPE.HARVEST} status={status} />
                <Id {...{ visible: !!keys[HARVEST_OPTION_KEY.ID], operation, status }} />
                <OperationStatus {...{ visible: !!keys[HARVEST_OPTION_KEY.OPERATION_STATUS], status }} />
                <Certain {...{ visible: !!keys[HARVEST_OPTION_KEY.CERTAIN], operation, status }} />
                <div className='flex flex-nowrap items-center [&>div]:whitespace-nowrap'>
                    <SiteUnit {...{ visible: !!keys[HARVEST_OPTION_KEY.SITE_UNIT], operation }} />
                    {(keys['site-unit'] && keys.factory) &&
                        <span className='arrow-right' />}
                    <Factory {...{ visible: !!keys[HARVEST_OPTION_KEY.FACTORY], operation }} />
                </div>
            </div>
            <div className={styles.right}>
                <Delay visible={true} operation={operation} />
                <Note {...{ visible: !!keys[HARVEST_OPTION_KEY.NOTE], operation }} />
                <RequireClosedTransport {...{
                    visible: !!keys[HARVEST_OPTION_KEY.REQUIRE_CLOSED_TRANSPORT],
                    operation,
                    vessel
                }} />
                <ExternalSite {...{
                    visible: !!keys[HARVEST_OPTION_KEY.EXTERNAL_SITE],
                    type: operation.source_site_type === SITE_TYPE.EXTERNAL
                }} />
                <Weather {...{
                    visible: !!keys[HARVEST_OPTION_KEY.WEATHER],
                    operation: operation as TOperation2
                }} />
                {!isOwn && <NameSpot {...{ operation }} />}
                <InvoiceCount operation={operation as TOperation2} />
            </div>
            <div className={`${styles.grading} absolute right-0 z-[1] flex flex-col items-end`}>
                {typeof harvestOperation.dead_fish === 'number' && <div className="h-[22px] max-h-[22px] overflow-hidden [&>div]:h-full [&>div>span:first-child]:hidden">
                    <DeadFish
                        value={harvestOperation.dead_fish}
                        total={totalDischargedFish}
                        valueClassName="text-[15px] !font-[400]"
                        showPercent={harvestOperation.status === OP_STATUS.FINISHED}
                    />
                </div>}
            </div>
        </div>
        <FishDisease diseases={operation.diseases} units={operation.sub_operations} wrapperClass={styles.section} />
        <LiceList diseases={operation.diseases} count={operation.sites?.length} short wrapperClass={styles.section} />
        {isSection1 && <div className={styles.section}>
            <SumWeight{...{ visible: !!keys[HARVEST_OPTION_KEY.SUM_WEIGHT], operation, vessel }} />
            <FishAmount{...{ visible: !!keys[HARVEST_OPTION_KEY.FISH_AMOUNT], operation }} />
        </div>}
        {isSection2 && <div className={styles.section}>
            <StartTime {...{
                visible: !!keys[HARVEST_OPTION_KEY.START_TIME],
                current_process: operation.current_process,
                activity_log
            }} />
            <ArrivalTimeAtSite {...{
                visible: keys[HARVEST_OPTION_KEY.ARRIVAL_TIME_AT_SITE],
                current_process: operation.current_process,
                activity_log
            }} />
            <ArrivalTimeAtFactory{...{
                visible: !!keys[HARVEST_OPTION_KEY.ARRIVAL_TIME_AT_FACTORY],
                current_process: operation.current_process,
                activity_log
            }} />
            <CompletedTime {...{
                visible: !!keys[HARVEST_OPTION_KEY.COMPLETED_TIME],
                current_process: operation.current_process,
                activity_log
            }} />
            <Duration {...{ visible: !!keys[HARVEST_OPTION_KEY.DURATION], activity_log }} />
        </div>}
        <NoteDetail operation={operation} visible={true} />
    </>;
};

export default HarvestView;
